import React, { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import {
  GetWaterProfilesByAccount,
  AlterActiveState,
} from "../../services/ClientWaterProfileService";
import ViewWaterProfile from "./ViewWaterProfile";
import CommonLoader from "../common/CommonLoader";

import { useHistory } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEye,
  faArrowLeft,
  faPlus,
  faCheckDouble,
} from "@fortawesome/free-solid-svg-icons";

const WaterProfilesTable = ({ accountNumber }) => {
  const columns = [
    { id: "id", label: "ID" },
    { id: "accountNumber", label: "Account" },
    { id: "type", label: "Type" },
    { id: "calcium", label: "Calcium" },
    { id: "totalAlkalinity", label: "Total Alkalinity" },
    { id: "bicarbonate", label: "Bicarbonate" },
    { id: "sulfate", label: "Sulfate" },
    { id: "magnesium", label: "Magnesium" },
    { id: "totalHardness", label: "Total Hardness" },
    { id: "iron", label: "Iron" },
    { id: "pH", label: "pH Level" },
    { id: "totalChlorine", label: "Total Chlorine" },
    { id: "residualAlkalinity", label: "Residual Alkalinity" },
    { id: "chloride", label: "Chloride" },
    { id: "sulfateToChlorideRatio", label: "Sulfate/Chloride Ratio" },
    { id: "sodium", label: "Sodium" },
    { id: "zinc", label: "Zinc" },
    { id: "potassium", label: "Potassium" },
    { id: "copper", label: "Copper" },
    { id: "tds", label: "TDS" },
    { id: "actions", label: "Actions" },
  ];

  const chemicalFields = [
    "calcium",
    "totalAlkalinity",
    "bicarbonate",
    "sulfate",
    "magnesium",
    "totalHardness",
    "iron",
    "pH",
    "totalChlorine",
    "residualAlkalinity",
    "chloride",
    "sulfateToChlorideRatio",
    "sodium",
    "zinc",
    "potassium",
    "copper",
    "tds",
  ];

  const rowsPerPageOptions = [5, 10, 25, 50, 100, 200];

  const [profilesData, setProfilesData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const history = useHistory();

  useEffect(() => {
    fetchProfiles();
  }, [accountNumber]);

  useEffect(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    setPaginatedData(profilesData.slice(start, end));
  }, [profilesData, page, rowsPerPage]);

  const fetchProfiles = async () => {
    if (accountNumber == undefined) {
      return;
    }
    try {
      setLoading(true);
      const response = await GetWaterProfilesByAccount(accountNumber);
      setProfilesData(response.results || []);
      setPaginatedData(response.results || []);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      NotificationManager.error("Error fetching profiles", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (profile) => setSelectedProfile(profile);

  const handleBackClick = () => {
    setSelectedProfile(null);
  };

  const handleToggleActive = async (profileId) => {
    try {
      setLoading(true);
      const response = await AlterActiveState(profileId);
      if (response.results === true) {
        NotificationManager.success(
          "Profile status updated successfully",
          "Success"
        );
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error updating profile status", "Error");
      }
    } catch (error) {
      NotificationManager.error("Error updating profile status", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProfileClick = () => history.push("/water-profiles/add");

  const handleEditProfileClick = (profile) =>
    history.push(`/water-profiles/${profile.id}`);

  const renderActions = (profile) => (
    <div
      className="buttons are-small has-addons"
      style={{ minWidth: "120px", display: "flex", gap: "4px" }}
    >
      <button
        className="button is-small is-info is-outlined"
        onClick={() => handleToggleActive(profile.id)}
        title={profile.isActive ? "Mark Inactive" : "Mark Active"}
      >
        <span className="icon is-small">
          <FontAwesomeIcon icon={faCheckDouble} />
        </span>
      </button>
      <button
        className="button is-small is-warning is-outlined"
        onClick={() => handleEditProfileClick(profile)}
        title="Edit Profile"
      >
        <span className="icon is-small">
          <FontAwesomeIcon icon={faEdit} />
        </span>
      </button>
      <button
        className="button is-small is-success is-outlined"
        onClick={() => handleViewClick(profile)}
        title="View Profile"
      >
        <span className="icon is-small">
          <FontAwesomeIcon icon={faEye} />
        </span>
      </button>
    </div>
  );

  const totalPages = Math.ceil(profilesData.length / rowsPerPage);

  return (
    <div className="container">
      <div className="section" style={{ padding: "0" }}>
        <div className="level">
          <div className="level-left">
            <div className="level-item">
              <h1 className="title is-3 has-text-primary">
                Water Quality Profiles
              </h1>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <button
                className="button is-primary"
                onClick={handleAddProfileClick}
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faPlus} />
                </span>
                <span>New Profile</span>
              </button>
            </div>
          </div>
        </div>

        <hr className="has-background-primary" style={{ height: "2px" }} />

        {selectedProfile ? (
          <div>
            <div className="mb-4">
              <button
                className="button is-primary is-outlined"
                onClick={handleBackClick}
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </span>
                <span>Back to List</span>
              </button>
            </div>
            <ViewWaterProfile profile={selectedProfile} />
          </div>
        ) : (
          <div>
            {/* Enhanced table container with sticky header and scrolling */}
            <div
              className="table-container"
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                border: "1px solid #e8e8e8",
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <table
                className="table is-fullwidth is-striped is-hoverable"
                style={{
                  marginBottom: "0",
                  borderCollapse: "separate",
                  borderSpacing: "0",
                }}
              >
                <thead>
                  <tr
                    className="has-background-light"
                    style={{
                      position: "sticky",
                      top: "0",
                      zIndex: "10",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    {columns.map((col, index) => (
                      <th
                        key={col.id}
                        className="has-text-weight-bold has-text-dark"
                        style={{
                          borderBottom: "2px solid #3273dc",
                          borderRight:
                            index < columns.length - 1
                              ? "1px solid rgba(0,0,0,0.08)"
                              : "none",
                          padding: "12px 8px",
                          backgroundColor: "#f5f5f5",
                          whiteSpace: "nowrap",
                          minWidth: col.id === "actions" ? "120px" : "auto",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((profile, rowIndex) => (
                      <tr
                        key={profile.id}
                        className={
                          rowIndex % 2 === 0 ? "" : "has-background-white-ter"
                        }
                        style={{
                          borderBottom: "1px solid rgba(0,0,0,0.05)",
                        }}
                      >
                        {columns.slice(0, -1).map((col, colIndex) => (
                          <td
                            key={col.id}
                            className="has-text-weight-normal"
                            style={{
                              padding: "10px 8px",
                              borderRight:
                                colIndex < columns.length - 2
                                  ? "1px solid rgba(0,0,0,0.05)"
                                  : "none",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {chemicalFields.includes(col.id)
                              ? profile[col.id]?.toFixed(2) || "N/A"
                              : profile[col.id] || "N/A"}
                          </td>
                        ))}
                        <td
                          style={{
                            padding: "10px 8px",
                            position: "sticky",
                            right: "0",
                            backgroundColor:
                              rowIndex % 2 === 0 ? "#ffffff" : "#fafafa",
                            borderLeft: "1px solid rgba(0,0,0,0.08)",
                            zIndex: "5",
                          }}
                        >
                          {renderActions(profile)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="has-text-centered has-text-grey-light"
                        style={{ padding: "40px" }}
                      >
                        {loading ? (
                          <div>
                            <span className="icon is-large has-text-primary">
                              <i className="fas fa-spinner fa-pulse"></i>
                            </span>
                            <p className="mt-2">Loading profiles...</p>
                          </div>
                        ) : (
                          <div>
                            <span className="icon is-large">
                              <FontAwesomeIcon icon={faEye} />
                            </span>
                            <p className="mt-2">No water profiles found</p>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Enhanced Pagination */}
            {profilesData.length > 0 && (
              <div className="level mt-4">
                <div className="level-left">
                  <div className="level-item">
                    <div className="field has-addons">
                      <div className="control">
                        <span className="button is-static">Rows per page:</span>
                      </div>
                      <div className="control">
                        <div className="select">
                          <select
                            value={rowsPerPage}
                            onChange={(e) => {
                              setRowsPerPage(parseInt(e.target.value, 10));
                              setPage(0);
                            }}
                          >
                            {rowsPerPageOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="level-item">
                    <p className="has-text-grey">
                      Showing {page * rowsPerPage + 1}-
                      {Math.min((page + 1) * rowsPerPage, profilesData.length)}{" "}
                      of {profilesData.length}
                    </p>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <nav className="pagination" role="navigation">
                      <button
                        className="pagination-previous"
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                      >
                        Previous
                      </button>
                      <button
                        className="pagination-next"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(page + 1)}
                      >
                        Next
                      </button>
                      <ul className="pagination-list">
                        {Array.from(
                          { length: Math.min(totalPages, 5) },
                          (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i;
                            } else if (page < 3) {
                              pageNum = i;
                            } else if (page >= totalPages - 3) {
                              pageNum = totalPages - 5 + i;
                            } else {
                              pageNum = page - 2 + i;
                            }

                            return (
                              <li key={pageNum}>
                                <button
                                  className={`pagination-link ${
                                    page === pageNum ? "is-current" : ""
                                  }`}
                                  onClick={() => setPage(pageNum)}
                                >
                                  {pageNum + 1}
                                </button>
                              </li>
                            );
                          }
                        )}
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <CommonLoader loading={loading} />
      </div>
    </div>
  );
};

export default WaterProfilesTable;
